"use client";

import { parseAsString, useQueryState } from "nuqs";
import { useMemo } from "react";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import type { TimelineStep } from "@/types";

/** What a step asks of one 3D layer: a visibility filter, a clipping volume, or both. */
export interface StepLayerStyle {
  layerId: string;
  /** Mapbox-style expression deciding which features of the layer are shown. */
  show?: string;
  /** GeoJSON the layer is clipped to; absent means "clip to nothing". */
  clipping?: unknown;
}

/** Steps that name themselves, since the id is what the URL carries. */
export function usableSteps(steps: TimelineStep[] | undefined): TimelineStep[] {
  return (steps ?? []).filter((step) => !!step.id);
}

/** The step the URL names, falling back to the first one. */
export function currentStep(
  steps: TimelineStep[],
  stepId: string | null
): TimelineStep | undefined {
  return steps.find((step) => step.id === stepId) ?? steps[0];
}

/**
 * How each 3D layer should look at `step`.
 *
 * One list rather than the legacy pair of objects keyed by layer id: the two were
 * built from the same array and always read together, and keeping them apart made
 * it possible for a layer to appear in one and not the other.
 */
export function stepLayerStyles(step: TimelineStep | undefined): StepLayerStyle[] {
  return (step?.layers ?? []).flatMap((layer) =>
    layer.layerId
      ? [
          {
            layerId: layer.layerId,
            show: typeof layer.filter === "string" ? layer.filter : undefined,
            clipping: layer.clipping,
          },
        ]
      : []
  );
}

/**
 * The project's timeline, and which step is showing.
 *
 * The step lives in the URL, so a shared link reproduces the year the sender was
 * looking at — the same reasoning as the camera and the selected POI.
 */
export function useTimeline() {
  const { timeline } = useProjectConfigs();
  const [stepId, setStepId] = useQueryState("timeline", parseAsString);

  const steps = useMemo(() => usableSteps(timeline?.steps), [timeline?.steps]);
  const step = useMemo(() => currentStep(steps, stepId), [steps, stepId]);

  return {
    steps,
    step,
    // Not `stepId`: with no parameter in the URL the first step is showing, and
    // the stepper has to mark it as such.
    activeStepId: step?.id ?? null,
    setStepId,
    layerStyles: useMemo(() => stepLayerStyles(step), [step]),
  };
}
